import React, { useEffect, useState } from "react";

import { TFunction, useTranslation } from "utils";

import { CoverCropDetailsData, CoverCropWithScores } from "models";

import { Collapse, Drawer, Tabs } from "components";

type DrawerTabs = {
  key: string;
  label: string;
  children: React.ReactNode;
};

const coverCropDetailsKeys = {
  cropProperties: {
    title: "cropProperties.title",
    items: ["growingPeriod", "rotationCategory", "minGrowthPeriod", "matchCrop", "nitrogen"],
  },
  farm: {
    title: "farm.title",
    items: ["droughtProne", "soilPh", "soilType"],
  },
  farmProfilePreferences: {
    title: "farmProfilePreferences.title",
    items: ["fodder", "biogas", "costCategory"],
  },
};

export const DigitalAgronomistDrawer = ({
  showDrawer,
  setShowDrawer,
  coverCropWithScores,
}: {
  showDrawer: boolean;
  setShowDrawer: (show: boolean) => void;
  coverCropWithScores?: CoverCropWithScores;
}) => {
  const { t } = useTranslation("digitalAgronomist");
  const [tabItems, setTabItems] = useState<DrawerTabs[]>([]);

  useEffect(() => {
    if (coverCropWithScores) {
      const tabs = Object.keys(coverCropDetailsKeys).map((key) =>
        generateDrawerTab(key as keyof typeof coverCropDetailsKeys, coverCropWithScores.coverCropDetails, t)
      );
      setTabItems(tabs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverCropWithScores]);

  if (!coverCropWithScores) return null;

  return (
    <Drawer
      className="digital-agronomist-drawer-cover-crops"
      dataTestId="da-cover-crop-drawer"
      visible={showDrawer}
      title={t("recommendationTableDrawer.title")}
      onClose={() => setShowDrawer(false)}
    >
      <h2 data-testid="da-drawer-header">
        {t("recommendationTableDrawer.subtitle", {
          coverCropName: coverCropWithScores.coverCropName,
          coverCropScore: coverCropWithScores.coverCropScore,
        })}
      </h2>
      <p className="margin-0 padding-top-100 padding-bottom-100">{t("recommendationTableDrawer.description")}</p>
      <Tabs defaultActiveKey="cropProperties" items={tabItems} />
    </Drawer>
  );
};

const generateDrawerTab = (tabName: keyof typeof coverCropDetailsKeys, coverCropDetailsData: CoverCropDetailsData, t: TFunction) => {
  const emptyTab = (
    <p className="paragraph-m" style={{ textAlign: "center" }} data-testid={`da-cover-crop-drawer-tab-${tabName}-no-details`}>
      {t("recommendationTableDrawer.noData")}
    </p>
  );

  let itemCount = 0;
  const children = coverCropDetailsKeys[tabName].items
    .map((item) => {
      const show = Object.entries(coverCropDetailsData).some(([key, value]) => key === item && value);
      if (!show) return;
      itemCount += 1;
      return (
        <Collapse
          key={item}
          defaultActiveKey={itemCount < 4 ? item : ""}
          items={[
            {
              key: item,
              label: (
                <h3 data-testid={`da-cover-crop-drawer-collapse-${item}-title`}>{t(`recommendationTableDrawer.coverCropCalculationDetails.${item}.title`)}</h3>
              ),
              children: (
                <p data-testid={`da-cover-crop-drawer-collapse-${item}-description`}>
                  {t(`recommendationTableDrawer.coverCropCalculationDetails.${item}.description`)}
                </p>
              ),
            },
          ]}
        />
      );
    })
    .filter(Boolean);

  const tab = {
    key: tabName,
    label: t(`recommendationTableDrawer.${tabName}Tab`),
    disabled: false,
    children: <div>{children.length > 0 ? children : emptyTab}</div>,
  };

  return tab;
};
